import React from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { scrollToMap, LogosGroup } from '@components';
import {
    StyledRoot,
    StyledInner,
    StyledEyebrow,
    StyledHeading,
    StyledBody,
    StyledStatsPanel,
    StyledStatCell,
    StyledStatNumber,
    StyledStatLabel,
    StyledCtaButton,
    StyledFooter,
} from './unscr2286-hero-section.styled';
import { formatNumber } from '@helpers';
import { useCountUpAnimation } from 'hooks';
import { logos } from '@constants';

interface Stat {
    number: string;
    label: string;
}

interface Properties {
    isLoading: boolean;
    globalStatistics;
}

export default function UNSCR2286HeroSection({
    isLoading,
    globalStatistics,
}: Properties): JSX.Element {
    const animatedTotal = useCountUpAnimation(
        18000,
        globalStatistics.current?.totalIncidents,
        isLoading
    );
    const animatedDamaged = useCountUpAnimation(
        4000,
        globalStatistics.current?.healthFacilitiesDamagedDestroyed,
        isLoading
    );
    const animatedKilled = useCountUpAnimation(
        3500,
        globalStatistics.current?.healthWorkersKilled,
        isLoading
    );

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

    const stats: Stat[] = [
        {
            number: formatNumber(animatedTotal),
            label: 'total attacks on health care systems',
        },
        {
            number: formatNumber(animatedDamaged),
            label: 'attacks damaged or destroyed hospitals and clinics',
        },
        {
            number: formatNumber(animatedKilled),
            label: 'health workers killed',
        },
    ];

    return (
        <StyledRoot>
            <StyledInner>
                <StyledEyebrow>
                    <a
                        href="https://unscr.com/en/resolutions/2286/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        UN Security Council Resolution 2286
                    </a>
                    {' · A decade of data by '}
                    <a
                        href="https://insecurityinsight.org"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Insecurity Insight
                    </a>
                </StyledEyebrow>

                <StyledHeading component="h1">
                    10 years since UNSCR 2286
                </StyledHeading>

                <StyledBody>
                    In May 2016, the UN Security Council unanimously adopted{' '}
                    <a
                        href="https://unscr.com/en/resolutions/2286/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Resolution 2286
                    </a>{' '}
                    to protect health care in conflict.
                    <br />
                    This commitment has failed to translate into safety.
                </StyledBody>

                <StyledStatsPanel container wrap="wrap">
                    {stats.map(stat => (
                        <StyledStatCell item key={stat.label} xs={12} sm={4}>
                            <StyledStatNumber>{stat.number}</StyledStatNumber>
                            <StyledStatLabel>{stat.label}</StyledStatLabel>
                        </StyledStatCell>
                    ))}
                </StyledStatsPanel>

                <StyledCtaButton
                    variant="contained"
                    disableElevation
                    fullWidth={isMobile}
                    onClick={scrollToMap}
                >
                    Explore the map
                </StyledCtaButton>
            </StyledInner>

            <StyledFooter>
                <LogosGroup
                    logos={logos.mainLogos}
                    invert={true}
                    logoWidth="100px"
                    smallGap
                ></LogosGroup>
            </StyledFooter>
        </StyledRoot>
    );
}
